import { getComparisonPeriod, type ComparisonMode } from "./dateUtils";

const DAY_MS = 86_400_000;

export const MANUAL_VIEW_REFRESH_CONFIRM_DAYS = 35;
export const MANUAL_VIEW_REFRESH_MAX_DAYS = 366;

export interface ManualViewRefreshRange {
  startDate: string;
  endDate: string;
}

function dateEpoch(value: string): number {
  return Date.parse(`${value}T00:00:00.000Z`);
}

function dateString(epoch: number): string {
  return new Date(epoch).toISOString().slice(0, 10);
}

function completedReportingBoundary(now: Date): string {
  const today = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  return dateString(today - DAY_MS);
}

function addRange(dates: Set<string>, range: ManualViewRefreshRange, boundary: string) {
  const start = dateEpoch(range.startDate);
  const end = Math.min(dateEpoch(range.endDate), dateEpoch(boundary));
  if (!Number.isFinite(start) || !Number.isFinite(end) || start > end) return;
  for (let cursor = start; cursor <= end; cursor += DAY_MS) {
    dates.add(dateString(cursor));
    // The caller rejects MAX + 1. Stop there so a malformed authenticated
    // request cannot make date planning itself enumerate years of history.
    if (dates.size > MANUAL_VIEW_REFRESH_MAX_DAYS) return;
  }
}

function contiguousRanges(dates: string[]): ManualViewRefreshRange[] {
  if (dates.length === 0) return [];
  const ranges: ManualViewRefreshRange[] = [];
  let startDate = dates[0]!;
  let endDate = startDate;
  for (const date of dates.slice(1)) {
    if (dateEpoch(date) === dateEpoch(endDate) + DAY_MS) {
      endDate = date;
      continue;
    }
    ranges.push({ startDate, endDate });
    startDate = date;
    endDate = date;
  }
  ranges.push({ startDate, endDate });
  return ranges;
}

// Exact completed dates represented by the current report. A visible comparison
// is included because refreshing only one side would leave the displayed delta
// internally inconsistent. Overlaps are deduplicated before provider jobs are
// planned, so week-over-week comparisons never request the same date twice.
export function planManualViewRefresh(args: {
  start: string;
  end: string;
  comparisonMode: ComparisonMode;
  now?: Date;
}): { ranges: ManualViewRefreshRange[]; days: number; requiresConfirmation: boolean } {
  const boundary = completedReportingBoundary(args.now ?? new Date());
  const dates = new Set<string>();
  addRange(dates, { startDate: args.start, endDate: args.end }, boundary);
  const comparison = getComparisonPeriod(args.start, args.end, args.comparisonMode);
  if (comparison && dates.size <= MANUAL_VIEW_REFRESH_MAX_DAYS) {
    addRange(dates, { startDate: comparison.start, endDate: comparison.end }, boundary);
  }
  const sortedDates = [...dates].sort();
  return {
    ranges: contiguousRanges(sortedDates),
    days: sortedDates.length,
    requiresConfirmation: sortedDates.length > MANUAL_VIEW_REFRESH_CONFIRM_DAYS,
  };
}
